import { useEffect, useRef, useState } from "react";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  TextH, PencilSimple, ArticleNyTimes,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ALLOWED_TAGS = new Set([
  "P", "BR", "DIV", "SPAN", "B", "STRONG", "I", "EM", "U",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "UL", "OL", "LI",
  "BLOCKQUOTE", "PRE", "CODE", "HR",
  "A", "SUB", "SUP",
  "TABLE", "THEAD", "TBODY", "TR", "TH", "TD",
  "IMG", "FIGURE", "FIGCAPTION",
]);

/** Turn stored value into HTML suitable for contentEditable. */
export function toEditorHtml(value) {
  const raw = (value || "").trim();
  if (!raw) return "<p><br></p>";
  if (/<[a-z][\s\S]*>/i.test(raw)) return sanitizeHtml(raw) || "<p><br></p>";
  return raw
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeText(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

function escapeText(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Keep useful rich-text tags; drop scripts/styles/classes/events. */
export function sanitizeHtml(html) {
  if (!html || typeof document === "undefined") return html || "";
  const wrap = document.createElement("div");
  wrap.innerHTML = html;

  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const tag = child.tagName;
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "META" || tag === "LINK") {
        child.remove();
        continue;
      }
      // Word / Google Docs wrappers → unwrap
      if (tag === "O:P" || tag === "FONT" || tag.startsWith("O:")) {
        while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
        child.remove();
        continue;
      }
      if (!ALLOWED_TAGS.has(tag)) {
        // Keep text children, drop unknown wrapper
        while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
        child.remove();
        continue;
      }
      // Strip attributes except safe href / img src+alt
      const attrs = Array.from(child.attributes || []);
      for (const a of attrs) {
        if (tag === "A" && a.name === "href") {
          const href = (a.value || "").trim();
          if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) child.removeAttribute("href");
          continue;
        }
        if (tag === "IMG" && (a.name === "src" || a.name === "alt")) {
          if (a.name === "src") {
            const src = (a.value || "").trim();
            if (!/^https?:\/\//i.test(src) && !src.startsWith("/")) child.removeAttribute("src");
          }
          continue;
        }
        child.removeAttribute(a.name);
      }
      // Normalize semantic tags
      if (tag === "STRONG") {
        const b = document.createElement("b");
        while (child.firstChild) b.appendChild(child.firstChild);
        child.replaceWith(b);
        walk(b);
        continue;
      }
      if (tag === "EM") {
        const i = document.createElement("i");
        while (child.firstChild) i.appendChild(child.firstChild);
        child.replaceWith(i);
        walk(i);
        continue;
      }
      walk(child);
    }
  };
  walk(wrap);

  // Convert Word-style "· " / "- " paragraphs into real lists when pasted as plain paragraphs
  normalizeFakeLists(wrap);

  return wrap.innerHTML.trim();
}

/** If pasted text used "- item" lines instead of <ul>, upgrade them. */
function normalizeFakeLists(root) {
  const blocks = Array.from(root.children);
  let i = 0;
  while (i < blocks.length) {
    const el = blocks[i];
    if (!el || el.nodeType !== 1) { i += 1; continue; }
    const text = (el.textContent || "").trim();
    const bullet = /^([•\-\*▪◦]|(\d+)[.)])\s+/.test(text);
    if (!bullet || !["P", "DIV"].includes(el.tagName)) { i += 1; continue; }
    const ordered = /^\d+[.)]\s+/.test(text);
    const list = document.createElement(ordered ? "ol" : "ul");
    let j = i;
    while (j < blocks.length) {
      const cur = blocks[j];
      const t = (cur.textContent || "").trim();
      const isOrd = /^\d+[.)]\s+/.test(t);
      const isBul = /^([•\-\*▪◦])\s+/.test(t);
      if (!((ordered && isOrd) || (!ordered && isBul))) break;
      const li = document.createElement("li");
      li.textContent = t.replace(/^([•\-\*▪◦]|(\d+)[.)])\s+/, "");
      list.appendChild(li);
      j += 1;
    }
    el.replaceWith(list);
    for (let k = i + 1; k < j; k += 1) blocks[k]?.remove();
    blocks.splice(i, j - i, list);
    i += 1;
  }
}

/** Plain-text preview / length helper. */
export function htmlToPlain(html) {
  if (!html) return "";
  if (typeof document === "undefined") {
    return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.innerText || d.textContent || "").replace(/\u00a0/g, " ").trim();
}

function ToolbarBtn({ title, onMouseDown, active, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => {
        e.preventDefault(); // keep selection in the editor
        onMouseDown();
      }}
      className={`h-8 w-8 rounded-md grid place-items-center text-sm border border-transparent hover:bg-muted ${
        active ? "bg-muted border-border text-foreground" : "text-muted-foreground"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Compact field row: label + preview + Edit button that opens a rich HTML dialog.
 */
export function RichHtmlField({
  label,
  value,
  onChange,
  placeholder = "",
  hint = "",
  maxLength = 8000,
  testid = "rich-html-field",
  rowsHint = "large",
}) {
  const [open, setOpen] = useState(false);
  const plain = htmlToPlain(value);
  const preview = plain
    ? (plain.length > 140 ? `${plain.slice(0, 140)}…` : plain)
    : (placeholder || "Empty — click Edit to write");

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 rounded-lg text-xs gap-1.5"
          onClick={() => setOpen(true)}
          data-testid={`${testid}-edit`}
        >
          <PencilSimple className="w-3.5 h-3.5" />
          {plain ? "Edit" : "Add"}
        </Button>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full text-left rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${
          plain ? "text-foreground" : "text-muted-foreground italic"
        } ${rowsHint === "large" ? "min-h-[3.25rem]" : "min-h-[2.5rem]"}`}
        data-testid={`${testid}-preview`}
      >
        <span className="line-clamp-3 whitespace-pre-wrap">{preview}</span>
      </button>
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
      <RichHtmlEditorDialog
        open={open}
        onOpenChange={setOpen}
        title={label}
        value={value || ""}
        maxLength={maxLength}
        placeholder={placeholder}
        onSave={(html) => {
          onChange(html);
          setOpen(false);
        }}
        testid={`${testid}-dialog`}
      />
    </div>
  );
}

function ensureSelectionInEditor(editor) {
  if (!editor) return;
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) return;
  editor.focus();
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/** Convert selected lines (or current block) into a real UL/OL when execCommand fails. */
function applyListManual(editor, ordered) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);

  let block = range.commonAncestorContainer;
  if (block.nodeType === Node.TEXT_NODE) block = block.parentElement;
  while (block && block !== editor && !/^(P|DIV|H[1-6]|LI)$/i.test(block.tagName || "")) {
    block = block.parentElement;
  }
  if (!block || block === editor) {
    const text = range.toString() || (editor.innerText || "").trim();
    if (!text) return false;
    const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return false;
    const list = document.createElement(ordered ? "ol" : "ul");
    lines.forEach((line) => {
      const li = document.createElement("li");
      li.textContent = line.replace(/^([•\-\*▪◦]|(\d+)[.)])\s+/, "");
      list.appendChild(li);
    });
    range.deleteContents();
    range.insertNode(list);
    return true;
  }

  if (block.tagName === "LI") {
    const parentList = block.parentElement;
    if (parentList && /^(UL|OL)$/i.test(parentList.tagName)) {
      if ((ordered && parentList.tagName === "OL") || (!ordered && parentList.tagName === "UL")) {
        document.execCommand(ordered ? "insertOrderedList" : "insertUnorderedList", false, null);
        return true;
      }
      const next = document.createElement(ordered ? "ol" : "ul");
      while (parentList.firstChild) next.appendChild(parentList.firstChild);
      parentList.replaceWith(next);
      return true;
    }
  }

  const list = document.createElement(ordered ? "ol" : "ul");
  const parts = (block.innerText || block.textContent || "")
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  (parts.length ? parts : [""]).forEach((line) => {
    const li = document.createElement("li");
    li.textContent = line.replace(/^([•\-\*▪◦]|(\d+)[.)])\s+/, "") || "\u00a0";
    list.appendChild(li);
  });
  block.replaceWith(list);
  return true;
}

const RICH_EDITOR_CSS = `
  .rich-html-editor ul {
    list-style-type: disc !important;
    padding-left: 1.5rem !important;
    margin: 0.4rem 0 !important;
  }
  .rich-html-editor ol {
    list-style-type: decimal !important;
    padding-left: 1.5rem !important;
    margin: 0.4rem 0 !important;
  }
  .rich-html-editor li {
    display: list-item !important;
    margin: 0.15rem 0;
  }
  .rich-html-editor h1, .rich-html-editor h2, .rich-html-editor h3 {
    font-weight: 700;
    margin: 0.6rem 0 0.3rem;
    line-height: 1.3;
  }
  .rich-html-editor h3 { font-size: 1.05rem; }
  .rich-html-editor h2 { font-size: 1.15rem; }
  .rich-html-editor h1 { font-size: 1.25rem; }
  .rich-html-editor p { margin: 0.35rem 0; }
  .rich-html-editor b, .rich-html-editor strong { font-weight: 700; }
  .rich-html-editor i, .rich-html-editor em { font-style: italic; }
  .rich-html-editor u { text-decoration: underline; }
  .rich-html-editor:empty:before,
  .rich-html-editor:has(> br:only-child):before,
  .rich-html-editor:has(> p:only-child > br:only-child):before {
    content: attr(data-placeholder);
    color: hsl(var(--muted-foreground));
    pointer-events: none;
  }
`;

/**
 * Inline rich HTML editor (toolbar + contentEditable). Use inside forms/modals.
 */
export function InlineRichHtmlEditor({
  value = "",
  onChange,
  maxLength = 100000,
  placeholder = "Start typing…",
  testid = "rich-html-inline",
  minHeightClass = "min-h-[240px]",
  maxHeightClass = "max-h-[50vh]",
}) {
  const editorRef = useRef(null);
  const seeded = useRef(false);
  const [plainLen, setPlainLen] = useState(0);

  useEffect(() => {
    const initial = toEditorHtml(value);
    if (editorRef.current) {
      editorRef.current.innerHTML = initial || "<p><br></p>";
      setPlainLen(htmlToPlain(editorRef.current.innerHTML).length);
      seeded.current = true;
    }
    // Mount-only seed; remount via key when switching documents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (html) => {
    const cleaned = sanitizeHtml(html);
    const plain = htmlToPlain(cleaned);
    setPlainLen(plain.length);
    onChange?.(plain ? cleaned : "");
  };

  const syncFromEditor = () => {
    if (!editorRef.current) return;
    emit(editorRef.current.innerHTML);
  };

  const run = (cmd, arg = null) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    ensureSelectionInEditor(ed);
    if (cmd === "formatBlock" && arg && !/^</.test(arg)) {
      document.execCommand(cmd, false, `<${arg}>`);
    } else {
      document.execCommand(cmd, false, arg);
    }
    syncFromEditor();
  };

  const toggleList = (ordered) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    ensureSelectionInEditor(ed);
    const cmd = ordered ? "insertOrderedList" : "insertUnorderedList";
    const before = ed.innerHTML;
    document.execCommand(cmd, false, null);
    if (ed.innerHTML === before) applyListManual(ed, ordered);
    syncFromEditor();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const htmlClip = cd.getData("text/html");
    const textClip = cd.getData("text/plain");
    let insert = "";
    if (htmlClip && /<[a-z][\s\S]*>/i.test(htmlClip)) {
      insert = sanitizeHtml(htmlClip);
    } else if (textClip) {
      const lines = textClip.replace(/\r\n/g, "\n").split("\n");
      const looksLikeList = lines.filter((l) => /^([•\-\*▪◦]|(\d+)[.)])\s+/.test(l.trim())).length >= 2;
      if (looksLikeList) {
        const ordered = lines.some((l) => /^\d+[.)]\s+/.test(l.trim()));
        const items = lines
          .map((l) => l.trim())
          .filter(Boolean)
          .map((l) => `<li>${escapeText(l.replace(/^([•\-\*▪◦]|(\d+)[.)])\s+/, ""))}</li>`)
          .join("");
        insert = ordered ? `<ol>${items}</ol>` : `<ul>${items}</ul>`;
      } else {
        insert = lines.map((l) => `<p>${escapeText(l) || "<br>"}</p>`).join("");
      }
    }
    if (!insert) return;
    const ok = document.execCommand("insertHTML", false, insert);
    if (!ok && editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const tmp = document.createElement("div");
        tmp.innerHTML = insert;
        const frag = document.createDocumentFragment();
        while (tmp.firstChild) frag.appendChild(tmp.firstChild);
        range.insertNode(frag);
      }
    }
    syncFromEditor();
  };

  const overLimit = plainLen > maxLength;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-1">
        <ToolbarBtn title="Bold" testid={`${testid}-bold`} onMouseDown={() => run("bold")}>
          <TextB className="w-4 h-4" weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn title="Italic" testid={`${testid}-italic`} onMouseDown={() => run("italic")}>
          <TextItalic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn title="Underline" testid={`${testid}-underline`} onMouseDown={() => run("underline")}>
          <TextUnderline className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn title="Heading" testid={`${testid}-heading`} onMouseDown={() => run("formatBlock", "h3")}>
          <TextH className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn title="Paragraph" testid={`${testid}-paragraph`} onMouseDown={() => run("formatBlock", "p")}>
          <span className="text-[10px] font-semibold">P</span>
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn title="Bulleted list" testid={`${testid}-ul`} onMouseDown={() => toggleList(false)}>
          <ListBullets className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn title="Numbered list" testid={`${testid}-ol`} onMouseDown={() => toggleList(true)}>
          <ListNumbers className="w-4 h-4" />
        </ToolbarBtn>
        <span className="ml-auto text-[11px] tabular-nums text-muted-foreground px-2">
          {plainLen.toLocaleString()} / {maxLength.toLocaleString()}
        </span>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-testid={`${testid}-editor`}
        className={`rich-html-editor ${minHeightClass} ${maxHeightClass} overflow-y-auto rounded-lg border border-border bg-background px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring`}
        onInput={syncFromEditor}
        onBlur={syncFromEditor}
        onPaste={onPaste}
      />

      <style>{RICH_EDITOR_CSS}</style>

      {overLimit && (
        <p className="text-xs text-destructive" data-testid={`${testid}-over-limit`}>
          Content is over the {maxLength.toLocaleString()} character limit. Shorten before saving.
        </p>
      )}
    </div>
  );
}

export default function RichHtmlEditorDialog({
  open,
  onOpenChange,
  title = "Edit",
  value = "",
  onSave,
  maxLength = 8000,
  placeholder = "Start typing…",
  testid = "rich-html-dialog",
}) {
  const [draft, setDraft] = useState("");
  const [plainLen, setPlainLen] = useState(0);

  useEffect(() => {
    if (!open) return;
    const initial = toEditorHtml(value);
    setDraft(initial);
    setPlainLen(htmlToPlain(initial).length);
  }, [open, value]);

  const overLimit = plainLen > maxLength;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[min(92vw,42rem)] max-h-[90vh] flex flex-col gap-3"
        data-testid={testid}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArticleNyTimes className="w-5 h-5 text-muted-foreground" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Paste keeps bold, headings, and bullets. The AI Agent reads the text content (formatting is for your editing).
          </DialogDescription>
        </DialogHeader>

        {open && (
          <InlineRichHtmlEditor
            key={`${testid}-${open ? "1" : "0"}-${(value || "").length}`}
            value={value}
            maxLength={maxLength}
            placeholder={placeholder}
            testid={`${testid}-editor`}
            onChange={(html) => {
              setDraft(html);
              setPlainLen(htmlToPlain(html).length);
            }}
          />
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => onOpenChange(false)}
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-lg"
            disabled={overLimit}
            onClick={() => {
              const cleaned = sanitizeHtml(draft);
              const plain = htmlToPlain(cleaned);
              onSave(plain ? cleaned : "");
            }}
            data-testid={`${testid}-save`}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
